import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { usePlaidLink } from 'react-plaid-link';
import { useFetch } from '@stashinvest/use-fetch';
import LinkPlaidLoading from './LinkPlaidLoading';
import LinkPlaidLinking from './LinkPlaidLinking';
import LinkPlaidError from './LinkPlaidError';
import LinkPlaidSelection from './LinkPlaidSelection';
import {
	trackOnSetupFailed,
	trackOnEvent,
	trackOnSetupClosed,
	trackOnSetupSubmitted,
} from './LinkPlaidEvents';
import {
	deleteBankLinkPlaidToken,
	displayCrossSellModal,
	getBankLinkPlaidTokenCookie,
	storeBankLinkOrigin,
	storeBankLinkPlaidToken,
} from '../utils';
import ACCOUNT_SUBTYPE from 'stash/constants/accountSubtypes';
import { ReauthConfirmBankWrapper } from './ReauthConfirm';
import { PlaidSuccessStates } from './PlaidSuccessStates';
import LinkPlaidReason from './LinkPlaidReason';
import LinkPlaidTimeout from './LinkPlaidTimeout';
import LinkBankRestricted, { RESTRICTED_USER_ERROR_CODE } from '../LinkBankRestricted';
import NoAccountsError from './NoAccountsError';
import useTrack from 'stash/hooks/useTrack';
import { hasGreaterThanZeroValue } from 'stash/components/reactivation/tier-selection/utils';

const LinkPlaid = ({
	successPath,
	originPath,
	onSuccess,
	onExit,
	onMicrosSelect,
	oauthRedirectUri,
}) => {
	const track = useTrack();
	const [plaidInitialized, setPlaidInitialized] = useState(false);
	const [plaidLinking, setPlaidLinking] = useState(false);
	const [linkId, setLinkId] = useState(null);
	const [success, setSuccess] = useState(false);
	const [error, setError] = useState(null);
	const [reason, setReason] = useState(null);
	const [restricted, setRestricted] = useState(false);
	const [plaidTimeout, setPlaidTimeout] = useState(false);
	const [plaidToken, setPlaidToken] = useState(null);
	const [maskedNumber, setMaskedNumber] = useState(null);
	const [accountName, setAccountName] = useState(null);
	const [accountType, setAccountType] = useState(null);
	const [multipleSelection, setMultipleSelection] = useState(false);
	const [accounts, setAccounts] = useState([]);
	const [publicToken, setPublicToken] = useState(null);
	const [noAccountsError, setNoAccountsError] = useState(false);
	const [isLinked, setIsLinked] = useState(false);
	const [bankAccountId, setBankAccountId] = useState(null);
	const [linkSessionId, setLinkSessionId] = useState(null);
	const [institutionName, setInstitutionName] = useState(null);

	const [showReauthFlow, setShowReauthFlow] = useState(false);
	const [showBillingSummary, setShowBillingSummary] = useState(false);

	const isOnboarding = successPath?.startsWith('/onboarding');

	const { fetch: getPlaidToken } = useFetch({
		method: useFetch.POST,
		path: '/apis/externalAccounts/api/v1/users/:uuid/link/plaid/launch',
		onError: (res) => {
			trackOnSetupFailed(isOnboarding, track);
			if (res?.errors?.[0]?.code === RESTRICTED_USER_ERROR_CODE) {
				setRestricted(true);
			} else {
				setError(res);
			}
		},
	});

	const { fetch: linkAccount } = useFetch({
		method: useFetch.POST,
		path: '/apis/externalAccounts/api/v1/users/:uuid/link/plaid/new',
		onError: (res) => {
			trackOnSetupFailed(isOnboarding, track);
			setError(res);
		},
	});

	const { fetch: recordLinkFailure } = useFetch({
		method: useFetch.POST,
		path: '/apis/externalAccounts/api/v1/users/:uuid/link/plaid/failures',
	});

	const {
		fetch: getReauthorizationData,
		data: reauthorizationData,
		error: reauthorizationError,
	} = useFetch({
		path: '/apis/autostash/api/v1/users/:uuid/automaticTransfersAuthorization?bankLink=plaid',
	});

	const { fetch: postReauth, error: postReauthError } = useFetch({
		path: '/apis/autostash/api/v1/users/:uuid/automaticTransfersAuthorization',
		method: 'POST',
	});

	const {
		fetch: getBillingSummary,
		data: billingSummaryData,
		error: billingSummaryError,
	} = useFetch({
		path: '/apis/customers/v1/users/:uuid/subscriptions/billingSummaries',
	});

	useEffect(() => {
		/**
		 * If onboarding, skip the reauth step and link the account.
		 * If reauth is required, show the reauth screen.
		 * If on a $0 tier, skip the reauth step and link the account.
		 * If billing summary is available, show the billing summary screen.
		 */
		if (isOnboarding && (!!reauthorizationData || !!reauthorizationError)) {
			linkSelectedAccount(bankAccountId, publicToken);
		} else if (
			reauthorizationData &&
			reauthorizationData.authorization.showAuthorizationScreen
		) {
			setShowReauthFlow(true);
		} else if (
			!!billingSummaryData &&
			(!!reauthorizationData || !!reauthorizationError) &&
			!hasGreaterThanZeroValue(
				billingSummaryData.billingSummary?.currentBillingPeriod?.billingFrequency?.amount
			)
		) {
			linkSelectedAccount(bankAccountId, publicToken);
		} else if (!!billingSummaryData || !!billingSummaryError) {
			setShowBillingSummary(true);
		}
	}, [reauthorizationData, accounts, publicToken, billingSummaryData]);

	const linkSelectedAccount = async (selectedAccountId, public_token = publicToken) => {
		trackOnSetupSubmitted(
			isOnboarding,
			track,
			selectedAccountId,
			linkSessionId,
			institutionName
		);

		const res = await linkAccount({
			body: {
				plaidPublicToken: public_token,
				plaidAccountId: selectedAccountId,
			},
		});
		setLinkId(res?.link?.linkId);
	};

	const showCrossSell = displayCrossSellModal(originPath);
	const shouldShowReauthorization = showReauthFlow;

	// Sets the plaid token
	useEffect(() => {
		// set cookie for smaug so that the plaid oauth redirect comes to app and not reg
		storeBankLinkOrigin();

		const storedBankLinkPlaidToken = getBankLinkPlaidTokenCookie();

		if (oauthRedirectUri && storedBankLinkPlaidToken) {
			setPlaidToken(storedBankLinkPlaidToken);
		} else {
			getPlaidToken().then((res) => {
				if (!!res?.token) {
					let token = res.tokenByVariant?.savings_account_linking || res.token;
					storeBankLinkPlaidToken(token);
					setPlaidToken(token);
				}
			});
		}
	}, [getPlaidToken, oauthRedirectUri]);

	const validateAccounts = (accounts, linkSessionId, institutionName) => {
		if (accounts.length > 1) {
			setAccounts((prev) => [...prev, ...accounts]);
			setMultipleSelection(true);
		} else if (accounts.length === 0) {
			setNoAccountsError(true);
		} else {
			setMaskedNumber(accounts[0].mask);
			setAccountName(accounts[0].name);
			setAccountType(accounts[0].subtype);
			setBankAccountId(accounts[0].id);
			setLinkSessionId(linkSessionId);
			setInstitutionName(institutionName);
		}
	};

	const onSuccessCallback = useCallback(
		(public_token, metadata) => {
			setPublicToken(public_token);
			setPlaidLinking(true);
			validateAccounts(
				metadata.accounts.filter(
					(account) =>
						account.subtype === ACCOUNT_SUBTYPE.CHECKING ||
						account.subtype === ACCOUNT_SUBTYPE.SAVINGS
				),
				metadata.link_session_id,
				metadata.institution_name || metadata.institution?.name || null
			);
			deleteBankLinkPlaidToken();

			/** Only fetch data for Plan Details card when not in onboarding */
			if (!isOnboarding) {
				getBillingSummary();
			}
			getReauthorizationData();
		},
		[getReauthorizationData]
	);

	const onExitCallback = (error, metadata) => {
		deleteBankLinkPlaidToken();
		trackOnSetupClosed(error, metadata, isOnboarding, track);
		if (error) {
			recordLinkFailure({
				body: {
					linkSessionId: metadata.link_session_id,
					plaidInstitutionId: metadata.institution?.institution_id,
					error: {
						errorType: error.error_type,
						errorCode: error.error_code,
					},
				},
			});
		}
		onExit();
	};

	const onLoadCallback = () => {
		// wait a few secs so the view doesn't switch while plaid opens up
		setTimeout(() => setPlaidInitialized(true), 2000);
	};

	const onEventCallback = (eventName, metadata) => {
		trackOnEvent(eventName, metadata, isOnboarding, track);
	};

	const config = {
		token: plaidToken,
		// pass in the received redirect URI, which contains an OAuth state ID parameter that is required to
		// re-initialize Link
		receivedRedirectUri: oauthRedirectUri,
		onSuccess: onSuccessCallback,
		onExit: onExitCallback,
		onEvent: onEventCallback,
		onLoad: onLoadCallback,
	};
	const { open, ready, error: plaidError } = usePlaidLink(config);

	if (!!plaidError) {
		setError(plaidError);
	}

	// automatically reinitialize Link
	useEffect(() => {
		if (ready) {
			open();
		}
	}, [ready, open]);

	return (
		<>
			{error ? (
				<LinkPlaidError
					error={error}
					linkAnotherBank={onExit}
					linkManually={onMicrosSelect}
					trackEvent={track}
					isOnboarding={isOnboarding}
				/>
			) : restricted ? (
				<LinkBankRestricted linkType="Plaid" />
			) : reason ? (
				<LinkPlaidReason reason={reason} dismiss={onExit} />
			) : plaidTimeout ? (
				<LinkPlaidTimeout
					dismiss={onExit}
					trackEvent={track}
					isOnboarding={isOnboarding}
				/>
			) : !plaidInitialized ? (
				<LinkPlaidLoading trackEvent={track} isOnboarding={isOnboarding} />
			) : success ? (
				<PlaidSuccessStates
					showCrossSell={showCrossSell}
					accountName={accountName}
					accountType={accountType}
					maskedNumber={maskedNumber}
					isLinked={isLinked}
					successPath={successPath}
					reauthorizationError={reauthorizationError}
					postReauthError={postReauthError}
				/>
			) : linkId ? (
				<LinkPlaidLinking
					linkId={linkId}
					setIsLinked={setIsLinked}
					onSuccess={() => {
						setSuccess(true);
						onSuccess();
					}}
					onError={() => setError(true)}
					onTimeout={() => setPlaidTimeout(true)}
					onReason={setReason}
					trackEvent={track}
					isOnboarding={isOnboarding}
				/>
			) : multipleSelection ? (
				<LinkPlaidSelection
					accounts={accounts}
					linkSelectedAccount={async (accountId) => {
						if (shouldShowReauthorization) {
							setBankAccountId(accountId);
							return;
						}
						await linkSelectedAccount(accountId);
					}}
					setMultipleSelection={setMultipleSelection}
					setAccountName={setAccountName}
					setMaskedNumber={setMaskedNumber}
				/>
			) : shouldShowReauthorization || showBillingSummary ? (
				<ReauthConfirmBankWrapper
					reauthorizationData={reauthorizationData}
					billingSummary={billingSummaryData?.billingSummary}
					accountName={accountName}
					maskedNumber={maskedNumber}
					bankLinkType="plaid"
					linkSelectedAccount={async (values) => {
						await linkSelectedAccount(bankAccountId, publicToken);
						if (!!values) await postReauth({ body: values });
						setShowReauthFlow(false);
						setShowBillingSummary(false);
					}}
				/>
			) : noAccountsError ? (
				<NoAccountsError linkAnotherBank={onExit} cancel={onExit} />
			) : plaidLinking ? (
				<LinkPlaidLoading trackEvent={track} isOnboarding={isOnboarding} />
			) : null}
		</>
	);
};
LinkPlaid.defaultProps = {
	onExit: () => {},
	successPath: '/',
};

LinkPlaid.propTypes = {
	onExit: PropTypes.func,
	onSuccess: PropTypes.func,
	onMicrosSelect: PropTypes.func,
	successPath: PropTypes.string,
	originPath: PropTypes.string,
	oauthRedirectUri: PropTypes.string,
	isOnboarding: PropTypes.bool,
};

export default LinkPlaid;
